/**
 * Shopify Swatch Image Switcher
 * ─────────────────────────────────────────────────────────────
 * Drop this into your theme's assets/ folder as swatch-image-switcher.js
 * Then include it in your product template via:
 *   {{ 'swatch-image-switcher.js' | asset_url | script_tag }}
 *
 * HOW IT WORKS
 * ────────────
 * When a customer clicks a color/pattern swatch:
 *   1. The main product image is replaced with the variant's image.
 *   2. All thumbnail images are replaced with that variant's media gallery.
 *   3. The active swatch gets a highlighted ring.
 *   4. The URL updates to ?variant=... so the page is shareable/bookmarkable.
 *
 * REQUIREMENTS
 * ────────────
 * Your swatch inputs must follow Shopify's standard variant radio pattern:
 *   <input type="radio" name="options[Color]" value="Floral" data-variant-id="123456" />
 *   or use <input type="radio" class="swatch-input" data-variant-id="..." />
 *
 * This script uses the Shopify storefront product JSON endpoint — no extra API key needed.
 * ─────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ── CONFIG ──────────────────────────────────────────────────
  // Selectors — adjust if your theme uses different class names
  const SELECTORS = {
    swatchInput:    'input[type="radio"][name^="options"]',   // all variant option radios
    mainImage:      '.product__media img, .product-single__photo img, [data-product-single-media-wrapper] img',
    mainImageWrap:  '.product__media, .product-single__photo, [data-product-single-media-wrapper]',
    thumbnailWrap:  '.product__thumbnails, .product-single__thumbnails, .thumbnails-wrapper',
    thumbnail:      '.product__thumbnail img, .product-single__thumbnail img, .thumbnail img',
    productForm:    'form[action*="/cart/add"]',
  };

  // ── UTILS ────────────────────────────────────────────────────
  function getProductHandle() {
    // Works on /products/* pages; falls back to window.meta if available
    if (window.ShopifyAnalytics?.meta?.product?.handle) {
      return window.ShopifyAnalytics.meta.product.handle;
    }
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function buildProductJsonUrl(handle) {
    return `/products/${handle}.js`;
  }

  // Cache fetched product data so we only hit the network once
  let _productData = null;

  async function fetchProductData(handle) {
    if (_productData) return _productData;
    try {
      const res = await fetch(buildProductJsonUrl(handle));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      _productData = await res.json();
      return _productData;
    } catch (err) {
      console.warn('[SwatchSwitcher] Could not fetch product JSON:', err);
      return null;
    }
  }

  // ── IMAGE SWAP ───────────────────────────────────────────────
  function setMainImage(src, alt) {
    if (!src) return;

    const mainImgs = document.querySelectorAll(SELECTORS.mainImage);
    if (!mainImgs.length) {
      // Fallback: swap the first <img> inside the product media section
      const fallback = document.querySelector('.product__media-item img, .product-featured-image');
      if (fallback) swapImgSrc(fallback, src, alt);
      return;
    }

    mainImgs.forEach(img => swapImgSrc(img, src, alt));
  }

  function swapImgSrc(img, src, alt) {
    // Fade transition
    img.style.transition = 'opacity 0.2s ease';
    img.style.opacity = '0';

    setTimeout(() => {
      // Handle Shopify CDN srcset pattern
      const base = src.split('?')[0];
      img.src = shopifySizedSrc(base, '800x');
      if (img.dataset.src)    img.dataset.src    = shopifySizedSrc(base, '800x');
      if (img.dataset.srcset) img.dataset.srcset = buildSrcset(base);
      if (img.srcset)         img.srcset         = buildSrcset(base);
      if (alt) img.alt = alt;
      img.style.opacity = '1';
    }, 180);
  }

  function shopifySizedSrc(url, size) {
    // Insert _800x before the file extension for Shopify image CDN
    return url.replace(/(\.[a-z]{3,4})(\?|$)/i, `_${size}$1$2`);
  }

  function buildSrcset(base) {
    const sizes = ['200x', '400x', '600x', '800x', '1000x', '1200x'];
    return sizes.map(s => `${shopifySizedSrc(base, s)} ${s.replace('x', 'w')}`).join(', ');
  }

  // Replace thumbnail strip with the variant's media
  function updateThumbnails(media) {
    if (!media || !media.length) return;

    const thumbWrap = document.querySelector(SELECTORS.thumbnailWrap);
    if (!thumbWrap) return;

    const existingThumbs = thumbWrap.querySelectorAll('img');

    media.forEach((item, i) => {
      const src = item.src || item.preview_image?.src;
      if (!src) return;

      const base = src.split('?')[0];

      if (existingThumbs[i]) {
        existingThumbs[i].src    = shopifySizedSrc(base, '150x');
        existingThumbs[i].srcset = buildSrcset(base);
      }
    });
  }

  // ── SWATCH ACTIVE STATE ──────────────────────────────────────
  function setActiveSwatchStyle(selectedInput) {
    // Remove active class from all sibling swatches in the same option group
    const name = selectedInput.name;
    document.querySelectorAll(`input[name="${CSS.escape(name)}"]`).forEach(el => {
      el.parentElement?.classList.remove('swatch--active');
      el.parentElement?.style && (el.parentElement.style.outline = '');
    });

    const label = selectedInput.closest('label') || selectedInput.parentElement;
    if (label) {
      label.classList.add('swatch--active');
      label.style.outline = '3px solid #222';
      label.style.outlineOffset = '2px';
    }
  }

  // ── URL UPDATE ───────────────────────────────────────────────
  function updateUrl(variantId) {
    if (!variantId || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  // ── CORE HANDLER ─────────────────────────────────────────────
  async function onSwatchChange(event) {
    const input = event.target;
    if (!input.matches(SELECTORS.swatchInput)) return;

    const handle = getProductHandle();
    if (!handle) return;

    setActiveSwatchStyle(input);

    const product = await fetchProductData(handle);
    if (!product) return;

    // Build the selected options map from all checked radios
    const selectedOptions = {};
    document.querySelectorAll(SELECTORS.swatchInput + ':checked').forEach(el => {
      const optionName = el.name.replace('options[', '').replace(']', '');
      selectedOptions[optionName] = el.value;
    });

    // Find the matching variant
    const variant = product.variants.find(v => {
      return v.options.every((optVal, idx) => {
        const key = product.options[idx];
        return !selectedOptions[key] || selectedOptions[key] === optVal;
      });
    });

    if (!variant) {
      console.warn('[SwatchSwitcher] No matching variant found for selected options.');
      return;
    }

    // Swap main image to variant's featured image
    const variantImg = variant.featured_image;
    if (variantImg) {
      setMainImage(variantImg.src, variantImg.alt || product.title);
    }

    // Update thumbnail strip with variant's media (filtered)
    const variantMedia = product.media?.filter(m =>
      !variantImg || m.position === variantImg.position ||
      variant.featured_image?.id === m.id
    ) || [];

    // If no filtered media, show all product media
    updateThumbnails(variantMedia.length ? variantMedia : product.media);

    // Update URL
    updateUrl(variant.id);

    // Update hidden variant ID input so Add to Cart works
    const variantIdInput = document.querySelector('input[name="id"], select[name="id"]');
    if (variantIdInput) variantIdInput.value = variant.id;

    // Dispatch a custom event for other scripts to hook into
    document.dispatchEvent(new CustomEvent('swatch:variantChanged', {
      detail: { variant, product }
    }));
  }

  // ── INIT ─────────────────────────────────────────────────────
  function init() {
    // Use event delegation on the document for resilience with dynamic forms
    document.addEventListener('change', onSwatchChange);

    // Highlight the swatch that matches the current URL variant on page load
    const urlParams = new URLSearchParams(window.location.search);
    const preselectedVariantId = urlParams.get('variant');
    if (preselectedVariantId) {
      const matchingInput = document.querySelector(
        `input[data-variant-id="${preselectedVariantId}"]`
      );
      if (matchingInput) setActiveSwatchStyle(matchingInput);
    }

    console.log('[SwatchSwitcher] Initialized ✓');
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();